import { HeadData } from '@/components/HeadData';
import type { FragmentType } from '@/gql/codegen';
import { StartPage } from '@/routes/_embaUserLayout/-components/StartPage';
import type React from 'react';
import { useState } from 'react';
import { CourseLessonPracticePageComponent } from '..';
import { useAnswerPracticeQuestionMutations } from '../-hooks/useAnswerPracticeQuestionMutations';
import type { COURSE_LESSON_PRACTICE_FRAGMENT } from '../index';

export type CourseLessonPracticeContainerProps = {
  content: FragmentType<typeof COURSE_LESSON_PRACTICE_FRAGMENT>;
  lessonName: string;
  chapterName: string;
  userAttendanceSlug: string;
  lessonSlug: string;
  onEmbaStartCourseLesson: () => Promise<void>;
  onEmbaCompleteCourseLesson: () => Promise<void>;
  headTitle: string;
};

export const CourseLessonPracticeContainer = ({
  content,
  lessonName,
  chapterName,
  userAttendanceSlug,
  lessonSlug,
  onEmbaStartCourseLesson,
  onEmbaCompleteCourseLesson,
  headTitle,
}: CourseLessonPracticeContainerProps) => {
  const {
    answerPracticeFreeInputQuestion,
    answerPracticeFreeInputQuestionError,
    answerPracticeFreeInputQuestionLoading,
    answerPracticeSelectQuestion,
    answerPracticeSelectQuestionError,
    answerPracticeSelectQuestionLoading,
    isAnsweredModalOpen,
    closeAnsweredModal,
  } = useAnswerPracticeQuestionMutations(userAttendanceSlug, lessonSlug);

  if (answerPracticeFreeInputQuestionError) {
    throw answerPracticeFreeInputQuestionError;
  }

  if (answerPracticeSelectQuestionError) {
    throw answerPracticeSelectQuestionError;
  }

  const [processState, setProcessState] = useState<'start' | 'in progress'>(
    'start',
  );

  const startLesson = () => {
    onEmbaStartCourseLesson();
    setProcessState('in progress');
  };

  const scrollToExplanation = () => {
    closeAnsweredModal();
    const element = document.getElementById('explanation');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <HeadData title={headTitle} />
      {processState === 'start' ? (
        <StartPage
          onClick={startLesson}
          chapterName={chapterName}
          lessonName={lessonName}
          lessonType="practice"
        />
      ) : (
        <CourseLessonPracticePageComponent
          content={content}
          lessonName={lessonName}
          chapterName={chapterName}
          onEmbaCompleteCourseLesson={onEmbaCompleteCourseLesson}
          answerPracticeFreeInputQuestion={answerPracticeFreeInputQuestion}
          answerPracticeSelectQuestion={answerPracticeSelectQuestion}
          isAnsweredModalOpen={isAnsweredModalOpen}
          closeAnsweredModal={closeAnsweredModal}
          scrollToExplanation={scrollToExplanation}
          answerPracticeFreeInputQuestionLoading={
            answerPracticeFreeInputQuestionLoading
          }
          answerPracticeSelectQuestionLoading={
            answerPracticeSelectQuestionLoading
          }
        />
      )}
    </>
  );
};
